import { useEffect, useState, type JSX } from 'react';
import { api, type RestartPrepViewDto } from '../api/client';
import { onNavClick } from '../lib/router';
import { RESTART_PREP_CHANGED, restartNavChip } from '../lib/restart-prep-view';
import '../styles/restart-prep.css';

/**
 * Reinício preparado (28/09/2026) — aviso de qualquer tela enquanto há preparação ou retomada em curso: com os motores
 * pausados e os turnos interrompidos, o dono precisa ver isso sem lembrar de abrir /reiniciar. Some sozinho 30 minutos
 * depois da retomada. Na barra (desktop) é um chip; no celular a barra não comporta mais um item e o aviso vive no topo
 * do "Mais", com o ponto de alerta no botão — o mesmo arranjo da pausa global.
 */
export function useRestartPrepView(): { view: RestartPrepViewDto | null; now: number } {
  const [view, setView] = useState<RestartPrepViewDto | null>(null);
  const [now, setNow] = useState(Date.now());
  const active = view?.active === true || view?.plan?.state === 'resuming';
  useEffect(() => {
    let alive = true;
    const load = (): void => {
      api.restartPrep().then((v) => { if (alive) { setView(v); setNow(Date.now()); } }).catch(() => undefined);
    };
    load();
    const timer = setInterval(load, active ? 10_000 : 60_000);
    // a tela /reiniciar publica cada leitura (e cada ação): o aviso acompanha na hora, sem esperar o próximo ciclo
    const onChange = (event: Event): void => {
      const detail = (event as CustomEvent<RestartPrepViewDto | undefined>).detail;
      if (detail) { setView(detail); setNow(Date.now()); } else load();
    };
    window.addEventListener(RESTART_PREP_CHANGED, onChange);
    return () => { alive = false; clearInterval(timer); window.removeEventListener(RESTART_PREP_CHANGED, onChange); };
  }, [active]);
  return { view, now };
}

export function restartChipFor(view: RestartPrepViewDto | null, now: number): ReturnType<typeof restartNavChip> {
  return restartNavChip(view?.plan ?? null, view?.readiness?.ready === true, now, view?.readiness?.caveat ?? null);
}

export function RestartPrepChip({ view, now, variant }: { view: RestartPrepViewDto | null; now: number; variant: 'bar' | 'more' }): JSX.Element | null {
  const chip = restartChipFor(view, now);
  if (!chip) return null;
  const tone = chip.tone === 'ok' ? ' restart-nav-chip--ok' : chip.tone === 'warn' ? ' restart-nav-chip--warn' : '';
  return (
    <a
      href="/reiniciar"
      onClick={onNavClick('/reiniciar')}
      className={`restart-nav-chip restart-nav-chip--${variant}${tone}`}
      data-testid={variant === 'bar' ? 'restart-nav-chip' : 'restart-nav-chip-more'}
      title="Reinício preparado do computador"
    >
      {chip.text}
    </a>
  );
}
